import { AssociateImageQuality } from '../imageQuality/model';

/** People Insight Engage, opened in a new tab from a scorecard row (no associate data in the URL). */
export const ENGAGE_URL = 'https://engage.na.people-insight.a2z.com/';

/** Sortable scorecard columns. */
export type ScorecardSortColumn = 'login' | 'retakeRate' | 'cycleTime' | 'skipRate';
export type SortDirection = 'ascending' | 'descending';

/** Image-quality columns the user can show or hide (Associate and Actions always show). */
export enum ScorecardColumn {
  RETAKE_RATE = 'retakeRate',
  CYCLE_TIME = 'cycleTime',
  SKIP_RATE = 'skipRate',
}

export const SCORECARD_COLUMNS: { key: ScorecardColumn; label: string }[] = [
  { key: ScorecardColumn.RETAKE_RATE, label: 'Retake Rate' },
  { key: ScorecardColumn.CYCLE_TIME, label: 'Cycle Time' },
  { key: ScorecardColumn.SKIP_RATE, label: 'Skip Rate' },
];

const metricValue = (
  associate: AssociateImageQuality,
  column: Exclude<ScorecardSortColumn, 'login'>
): number | undefined => {
  if (column === 'retakeRate') return associate.retakeRatePct;
  if (column === 'skipRate') return associate.skipRatePct;
  return associate.avgCycleTimeSeconds;
};

/**
 * A sorted copy; ties keep their incoming (worst-first) order. Associates without a value sort
 * last either way, so missing data never crowds the top of the list.
 */
export const sortScorecards = (
  associates: AssociateImageQuality[],
  column: ScorecardSortColumn | undefined,
  direction: SortDirection
): AssociateImageQuality[] => {
  if (!column) return associates;
  const sign = direction === 'ascending' ? 1 : -1;
  return associates
    .map((associate, incomingIndex) => ({ associate, incomingIndex }))
    .sort((left, right) => {
      // Logins are unique at a site, so they never tie.
      if (column === 'login')
        return left.associate.login.localeCompare(right.associate.login) * sign;
      const leftValue = metricValue(left.associate, column);
      const rightValue = metricValue(right.associate, column);
      const keepIncomingOrder = left.incomingIndex - right.incomingIndex;
      if (leftValue === undefined || rightValue === undefined) {
        return (
          (leftValue === undefined ? 1 : 0) - (rightValue === undefined ? 1 : 0) ||
          keepIncomingOrder
        );
      }
      return (leftValue - rightValue) * sign || keepIncomingOrder;
    })
    .map(({ associate }) => associate);
};

/** Associates whose login contains the search text (case-insensitive). */
export const searchByLogin = (
  associates: AssociateImageQuality[],
  query: string
): AssociateImageQuality[] => {
  const needle = query.trim().toLowerCase();
  return needle
    ? associates.filter(associate => associate.login.toLowerCase().includes(needle))
    : associates;
};
